import { Flex, Image } from "rebass/styled-components";
import styled, { css } from "styled-components";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useRef, useEffect, useState, useCallback } from "react";
import { GoTriangleRight } from "react-icons/go";
import { GoTriangleLeft } from "react-icons/go";

import {
  ActivePlaylistClipState,
  FallbackPlaylistTypeState,
  WatchedClipsCountState,
} from "MVVM/State/embeddablePlaylist";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { formatPlaylistClip, getPlayerName } from "../../Utilities";
import { PlaylistClip, PlaylistClips } from "../../@types";

const sharedArrowStyles = css`
  align-items: center;
  background: linear-gradient(270deg, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.5));
  cursor: pointer;
  height: 100%;
  justify-content: center;
  opacity: 0.5;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: opacity 300ms ease;
  width: 20px;
  z-index: 1;
`;

const Thumbnail = styled(Image)<{
  $active: boolean;
  $renderLolPlaylist: boolean;
}>`
  aspect-ratio: 16/9;
  border: ${({ $active, $renderLolPlaylist, theme }) => {
    if (!$active) return "1.5px solid inherit";
    return `1.5px solid ${$renderLolPlaylist ? "#3273FA" : theme.colors.envy}`;
  }};
  border-radius: 4px;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.4);
  flex: 0 0 auto;
  height: 100%;
  object-fit: cover;
`;

const StyledThumbnailContainer = styled(Flex)`
  -ms-overflow-style: none;
  cursor: pointer;
  flex-wrap: nowrap;
  gap: 12px;
  height: 100%;
  overflow-x: scroll;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ThumbnailRow = ({
  thumbnails,
  renderLolPlaylist,
  onWatchLimitReached,
}: {
  thumbnails: PlaylistClips;
  renderLolPlaylist: boolean;
  onWatchLimitReached?: (playerName: string, clickedClip: PlaylistClip) => void;
}) => {
  const [activePlaylistClip, setActivePlaylistClip] = useRecoilState(
    ActivePlaylistClipState,
  );
  const [watchedClipsCount, setWatchedClipsCount] = useRecoilState(
    WatchedClipsCountState,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const setFallbackPlaylistType = useSetRecoilState(FallbackPlaylistTypeState);
  const { track } = useAnalyticsContext();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      const isAtLeftEdge = container.scrollLeft === 0;
      const isAtRightEdge =
        container.scrollLeft + container.clientWidth >= container.scrollWidth;

      // Only prevent parent scroll if we can scroll horizontally
      if ((e.deltaY > 0 && !isAtRightEdge) || (e.deltaY < 0 && !isAtLeftEdge)) {
        e.preventDefault();
        e.stopPropagation();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, []);

  if (!thumbnails) return <></>;

  const handleThumbnailClick = (thumbnail: PlaylistClip) => {
    if (watchedClipsCount >= 2 && onWatchLimitReached) {
      const playerName = getPlayerName({
        metadata: thumbnail.metadata.map((m) => ({
          key: String(m.key || ""),
          value: String(m.value || ""),
        })),
      });
      onWatchLimitReached(playerName || thumbnail.username || "", thumbnail);

      return;
    }

    const formattedClip = formatPlaylistClip(thumbnail);

    track("Button - Click", {
      locationIdentifier: "thumbnail_click",
      location: "embeddable_playlist",
    });
    setFallbackPlaylistType("");
    setActivePlaylistClip(formattedClip);
    setWatchedClipsCount((prev) => prev + 1);
  };

  const updateArrows = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;

    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => updateArrows();
    el.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      el.removeEventListener("scroll", onScroll);
    };
  }, [updateArrows]);

  const scrollByAmount = (amount: number) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  const scrollPageLeft = () => {
    const el = containerRef.current;
    if (!el) return;
    scrollByAmount(-(el.clientWidth * 0.9));
  };

  const scrollPageRight = () => {
    const el = containerRef.current;
    if (!el) return;
    scrollByAmount(el.clientWidth * 0.9);
  };

  useEffect(() => {
    const onResize = () => updateArrows();
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [updateArrows]);

  return (
    <RowWrapper>
      {canScrollLeft && (
        <StyledArrowBackground className="arrow" onClick={scrollPageLeft}>
          <StyledArrowLeft />
        </StyledArrowBackground>
      )}
      <StyledThumbnailContainer ref={containerRef}>
        {thumbnails.map((thumbnail: PlaylistClip) => (
          <Thumbnail
            key={thumbnail._id}
            src={
              thumbnail.thumbnailOverlayUrl || thumbnail.thumbnailStandardUrl
            }
            onClick={() => handleThumbnailClick(thumbnail)}
            $renderLolPlaylist={renderLolPlaylist}
            $active={
              activePlaylistClip
                ? activePlaylistClip.internalId === thumbnail.internalId
                : false
            }
          />
        ))}
      </StyledThumbnailContainer>
      {canScrollRight && (
        <StyledArrowBackground2 className="arrow" onClick={scrollPageRight}>
          <StyledArrowRight />
        </StyledArrowBackground2>
      )}
    </RowWrapper>
  );
};

const RowWrapper = styled(Flex)`
  align-items: stretch;
  position: relative;
  overflow: hidden;

  &:hover .arrow {
    opacity: 1;
    pointer-events: auto;
  }
`;

const StyledArrowBackground = styled(Flex)`
  ${sharedArrowStyles}
  left: 0;
`;

const StyledArrowBackground2 = styled(Flex)`
  ${sharedArrowStyles}
  right: 0;
`;

const StyledArrowRight = styled(GoTriangleRight)`
  color: ${({ theme }) => theme.colors.ice};
  height: 20px;
  width: 20px;
`;

const StyledArrowLeft = styled(GoTriangleLeft)`
  color: ${({ theme }) => theme.colors.ice};
  height: 20px;
  width: 20px;
`;
